/**
 * Inbox NotificationType values (backend enum).
 * @see docs/DOKTOR-WEB-BILDIRIM-ENTEGRASYONU.md
 */
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/tr";

dayjs.extend(relativeTime);

export const INBOX_NOTIFICATION_TYPE = {
  NEW_MESSAGE: 0,
  APPOINTMENT_BOOKED: 1,
  APPOINTMENT_CANCELLED: 2,
  APPOINTMENT_RESCHEDULED: 3,
  APPOINTMENT_REMINDER: 4,
  SYSTEM_ANNOUNCEMENT: 5,
  MARKETING: 6,
};

const TYPE_LABEL_KEYS = {
  [INBOX_NOTIFICATION_TYPE.NEW_MESSAGE]: "notification_type_new_message",
  NewMessage: "notification_type_new_message",
  [INBOX_NOTIFICATION_TYPE.APPOINTMENT_BOOKED]: "notification_type_appointment_booked",
  AppointmentBooked: "notification_type_appointment_booked",
  [INBOX_NOTIFICATION_TYPE.APPOINTMENT_CANCELLED]: "notification_type_appointment_cancelled",
  AppointmentCancelled: "notification_type_appointment_cancelled",
  [INBOX_NOTIFICATION_TYPE.APPOINTMENT_RESCHEDULED]: "notification_type_appointment_rescheduled",
  AppointmentRescheduled: "notification_type_appointment_rescheduled",
  [INBOX_NOTIFICATION_TYPE.APPOINTMENT_REMINDER]: "notification_type_appointment_reminder",
  AppointmentReminder: "notification_type_appointment_reminder",
  [INBOX_NOTIFICATION_TYPE.SYSTEM_ANNOUNCEMENT]: "notification_type_system_announcement",
  SystemAnnouncement: "notification_type_system_announcement",
  [INBOX_NOTIFICATION_TYPE.MARKETING]: "notification_type_marketing",
  Marketing: "notification_type_marketing",
};

export const getNotificationTypeLabelKey = (type) => {
  if (type == null || type === "") return null;
  return TYPE_LABEL_KEYS[type] ?? TYPE_LABEL_KEYS[String(type)] ?? null;
};

const normalizeType = (notification) => {
  const dataType = notification?.data?.type ?? notification?.data?.Type;
  const type = notification?.type ?? notification?.Type;
  if (typeof type === "number") return type;
  if (typeof type === "string" && /^\d+$/.test(type)) return Number(type);

  const fromData = {
    NewMessage: INBOX_NOTIFICATION_TYPE.NEW_MESSAGE,
    AppointmentBooked: INBOX_NOTIFICATION_TYPE.APPOINTMENT_BOOKED,
    AppointmentCancelled: INBOX_NOTIFICATION_TYPE.APPOINTMENT_CANCELLED,
    AppointmentRescheduled: INBOX_NOTIFICATION_TYPE.APPOINTMENT_RESCHEDULED,
    AppointmentReminder: INBOX_NOTIFICATION_TYPE.APPOINTMENT_REMINDER,
    SystemAnnouncement: INBOX_NOTIFICATION_TYPE.SYSTEM_ANNOUNCEMENT,
    Marketing: INBOX_NOTIFICATION_TYPE.MARKETING,
  };
  if (typeof type === "string" && fromData[type] != null) return fromData[type];
  if (typeof dataType === "string" && fromData[dataType] != null) return fromData[dataType];
  return type;
};

const getDataValue = (data, ...keys) => {
  if (!data || typeof data !== "object") return undefined;
  for (const key of keys) {
    if (data[key] != null && data[key] !== "") return data[key];
  }
  return undefined;
};

/**
 * Returns in-app route for a notification, or null if none.
 */
export const getNotificationNavigatePath = (notification) => {
  const data = notification?.data ?? notification?.Data ?? {};
  const type = normalizeType(notification);

  const messageRoom = getDataValue(data, "messageRoom", "MessageRoom");
  const appointmentId = getDataValue(
    data,
    "appointmentId",
    "AppointmentId",
    "bookedAppointmentId",
    "BookedAppointmentId"
  );
  const deepLink = getDataValue(data, "deepLink", "DeepLink");

  if (type === INBOX_NOTIFICATION_TYPE.NEW_MESSAGE && messageRoom) {
    return `/messages/${messageRoom}`;
  }

  if (
    [
      INBOX_NOTIFICATION_TYPE.APPOINTMENT_BOOKED,
      INBOX_NOTIFICATION_TYPE.APPOINTMENT_CANCELLED,
      INBOX_NOTIFICATION_TYPE.APPOINTMENT_RESCHEDULED,
      INBOX_NOTIFICATION_TYPE.APPOINTMENT_REMINDER,
    ].includes(type) &&
    appointmentId
  ) {
    return `/appointments/detail/${appointmentId}`;
  }

  if (messageRoom) return `/messages/${messageRoom}`;
  if (appointmentId) return `/appointments/detail/${appointmentId}`;
  if (typeof deepLink === "string" && deepLink.startsWith("/")) return deepLink;

  return null;
};

export const parseNotificationList = (response) => {
  const raw = response?.data ?? response?.Data ?? response ?? [];
  return Array.isArray(raw) ? raw : [];
};

/** Navbar zil ikonu için inbox sorgusu */
export const NAVBAR_NOTIFICATION_QUERY = {
  page: 1,
  pageSize: 20,
  isRead: false,
};

export const mapInboxNotificationToNavItem = (notification) => ({
  id: notification?.id ?? notification?.Id,
  title: notification?.title ?? notification?.Title ?? "",
  body: notification?.body ?? notification?.Body ?? "",
  data: notification?.data ?? notification?.Data ?? {},
  createdAt: notification?.createdAt ?? notification?.CreatedAt,
  isRead: Boolean(notification?.isRead ?? notification?.IsRead),
  type: notification?.type ?? notification?.Type,
});

const getNavbarNotificationKey = (item) => {
  const data = item?.data ?? {};
  return (
    data.notificationId ??
    data.NotificationId ??
    item?.id ??
    null
  );
};

/** FCM kuyruğu + inbox listesini tek menü listesinde birleştirir */
export const mergeNavbarNotifications = (liveNotifications, inboxNotifications) => {
  const seen = new Set();
  const merged = [];

  const push = (item) => {
    const key = getNavbarNotificationKey(item);
    if (key == null || seen.has(String(key))) return;
    seen.add(String(key));
    merged.push(item);
  };

  (inboxNotifications || []).forEach((n) => push(mapInboxNotificationToNavItem(n)));
  (liveNotifications || []).forEach(push);

  return merged;
};

/** FCM / nav item mesaj odasına mı ait */
export const isMessageRelatedNotification = (notification) => {
  const data = notification?.data ?? notification?.Data ?? {};
  return !!(
    data.messageRoom ||
    data.MessageRoom ||
    normalizeType(notification) === INBOX_NOTIFICATION_TYPE.NEW_MESSAGE
  );
};

/** FCM payload → navbar / Redux bildirim öğesi */
export const parseFcmPayloadToNavItem = (payload) => {
  const data = payload?.data ?? {};
  const fromData = data.notificationId ?? data.NotificationId;
  const isUuid = (value) =>
    value != null && /^[0-9a-f-]{36}$/i.test(String(value));
  const id = isUuid(fromData)
    ? String(fromData)
    : payload?.messageId ?? `msg-${Date.now()}`;
  const title = payload?.notification?.title ?? data.title ?? data.Title ?? "";
  const body = payload?.notification?.body ?? data.body ?? data.Body ?? "";
  return { id, title, body, data, isRead: false };
};

/** Inbox API için bildirim kimliği (FCM messageId kullanılmaz) */
export const getNotificationInboxId = (notification) => {
  if (!notification) return null;
  const data = notification?.data ?? notification?.Data ?? {};
  const fromData = data.notificationId ?? data.NotificationId;
  const id = notification?.id ?? notification?.Id;
  const isUuid = (value) =>
    value != null && /^[0-9a-f-]{36}$/i.test(String(value));

  if (isUuid(fromData)) return String(fromData);
  if (isUuid(id)) return String(id);
  return null;
};

export const formatNotificationDate = (createdAt, language) => {
  if (!createdAt) return "";
  const locale = language?.startsWith("tr") ? "tr" : "en";
  const date = dayjs(createdAt).locale(locale);
  return `${date.format("D MMMM YYYY • HH:mm")} · ${date.fromNow()}`;
};
